const API_BASE = import.meta.env.VITE_API_BASE_URL
  ? `${import.meta.env.VITE_API_BASE_URL}/api/v1`
  : "/api/v1";

interface ApiErrorBody {
  error: string;
  details?: unknown;
}

export class ApiRequestError extends Error {
  constructor(
    public status: number,
    message: string,
    public details?: unknown,
  ) {
    super(message);
    this.name = "ApiRequestError";
  }
}

/**
 * Extract the CF Access JWT from the CF_Authorization cookie.
 * Requires HTTP Only to be OFF in the CF Access application settings.
 * The JWT is then passed as a header to the API (which is NOT behind
 * CF Access) for validation via JWKS.
 */
function getCfAccessJwt(): string | null {
  const match = document.cookie
    .split(";")
    .map((c) => c.trim())
    .find((c) => c.startsWith("CF_Authorization="));
  return match ? match.substring("CF_Authorization=".length) : null;
}

class ApiClient {
  private baseUrl: string;

  constructor(baseUrl = API_BASE) {
    this.baseUrl = baseUrl;
  }

  private async request<T>(path: string, options: RequestInit = {}): Promise<T> {
    const url = `${this.baseUrl}${path}`;
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      ...(options.headers as Record<string, string>),
    };

    // Read JWT from CF Access cookie and pass as explicit header.
    // The API worker validates this JWT against CF Access JWKS.
    const jwt = getCfAccessJwt();
    if (jwt) {
      headers["Cf-Access-Jwt-Assertion"] = jwt;
    }

    const response = await fetch(url, {
      ...options,
      headers,
    });

    if (!response.ok) {
      const body = (await response.json().catch(() => ({
        error: response.statusText,
      }))) as ApiErrorBody;
      throw new ApiRequestError(
        response.status,
        body.error || response.statusText,
        body.details,
      );
    }

    return response.json() as Promise<T>;
  }

  async get<T>(path: string): Promise<T> {
    return this.request<T>(path, { method: "GET" });
  }

  async post<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>(path, {
      method: "POST",
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  async put<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>(path, {
      method: "PUT",
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  async delete<T>(path: string): Promise<T> {
    return this.request<T>(path, { method: "DELETE" });
  }
}

export const api = new ApiClient();
